<template>
  <form class="space-y-6" @submit="onSubmit">
    <div class="flex justify-center p-4">
      <IconBeepaidCustomers />
    </div>

    <FormInputText name="name" label="Name" />

    <FormInputText name="email" label="Email" />

    <FormInputText name="phone" label="Phone Number" />

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      <template v-if="customerId">
        Update Customer
      </template>
      <template v-else> Create Customer </template>
    </FormSubmitButton>
  </form>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"

const props = defineProps<{
  customerId?: string
}>()

const router = useRouter()

const createEditCustomerFormSchema = z
  .object({
    name: z
      .string()
      .min(1, "Name must not be empty")
      .max(50, "Name too long"),
    email: z.union([
      z.literal(""),
      z
        .string()
        .email("Please enter a valid email address")
        .nullable(),
    ]),

    phone: z.string().nullable(),
  })
  .superRefine((params, ctx) => {
    if (!params.email && !params.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either email or phone is required",
        path: ["email"],
      })

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either email or phone is required",
        path: ["phone"],
      })
    }
  })

const { handleSubmit, isSubmitting, resetForm, setErrors } =
  useForm({
    validationSchema: toTypedSchema(
      createEditCustomerFormSchema
    ),
    initialValues: {
      name: "",
      email: "",
      phone: "",
    },
  })

const onSubmit = handleSubmit(async (params) => {
  try {
    props.customerId
      ? await editCustomer(props.customerId, params)
      : await createCustomer(params)

    toast.success(
      props.customerId
        ? "Customer Updated!"
        : "Customer Created!"
    )
    router.push({
      name: "customersIndex",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.customerId
        ? "Failed to update customer"
        : "Failed to create customer"
    )
  }
})

const isRetrievingCustomer = ref(false)
onMounted(async () => {
  if (props.customerId) {
    try {
      isRetrievingCustomer.value = true
      resetForm({
        values: await retrieveCustomer(props.customerId),
      })
    } catch {
      toast.error("Customer not found")
      router.push({
        name: "customersIndex",
      })
    } finally {
      isRetrievingCustomer.value = false
    }
  }
})
</script>
